import { Error } from "@coworker/apprestructured/src/login/components/Error/Error";
import { UnauthorizedPage } from "@coworker/apprestructured/src/login/components/Unauthorized/UnauthorizedPage";
import {
  useUserHasFixaBasicPermissions,
  useUserHasRoleOnPlatformOwner,
} from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import { ErrorType } from "@coworker/apprestructured/src/shared/constants/errorTypes";
import { useAuthorizeUser } from "@coworker/apprestructured/src/shared/hooks/useAuthorizeUser";
import { SimpleDefaultLoadingScreen } from "@coworker/apprestructured/src/shared/simple/SimpleLoading/SimpleDefaultLoadingScreen";
import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";
import { ReactElement } from "react";
import { Navigate } from "react-router";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import {
  addSentryBreadcrumb,
  reportMessageToSentry,
} from "@coworker/app/src/hooks/useConfigureSentry";
import { isMfeMode } from "@coworker/app/src/core/auth/useAuth";

export const AuthorizedUserRoute = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { pathname } = useFixaLocation();
  // In MFE mode the shell has already authenticated and authorized the user.
  // Skip Atkomst-based permission checks — the shell token does not carry
  // Atkomst claims and those checks will fail
  const mfeMode = isMfeMode();

  // TO-DO: COAPP-12897 - remove the feature flag once deployed to all markets.
  const enableAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);
  const atkomstOnboardingPaths = new Set(["/newuser", "/newuserteamselection"]);
  const isOnAtkomstOnboardingPath = atkomstOnboardingPaths.has(pathname);
  const shouldRunAuthorizeUser = !mfeMode && !isOnAtkomstOnboardingPath;
  const userState = useAuthorizeUser(shouldRunAuthorizeUser);

  // Always check for fixa.basic permission from the Atkomst JWT.
  // Skipped in MFE mode — the shell token has no Atkomst claims.
  const {
    data: hasFixaBasicPermissions,
    isLoading: isFixaBasicPermissionsLoading,
    isError: isFixaBasicPermissionsError,
  } = useUserHasFixaBasicPermissions(!mfeMode);

  const {
    data: hasPlatformOwnerAuthorization,
    isLoading: isPlatformOwnerLoading,
    isError: isPlatformOwnerError,
  } = useUserHasRoleOnPlatformOwner(!mfeMode);

  if (
    (shouldRunAuthorizeUser &&
      (userState.isAuthorizing || userState.isLoading)) ||
    isFixaBasicPermissionsLoading ||
    isPlatformOwnerLoading
  ) {
    return <SimpleDefaultLoadingScreen />;
  }

  // Breadcrumb of the resolved inputs feeding the routing decisions below —
  // helps reconstruct which branch was taken when investigating an issue in Sentry.
  addSentryBreadcrumb(
    "AuthorizedUserRoute: evaluating routing decision",
    "auth-routing",
    {
      pathname,
      enableAtkomst,
      hasFixaBasicPermissions,
      hasPlatformOwnerAuthorization,
      userStateIsError: userState.isError,
      userStateErrorType: userState.errorType,
      userStateIsActive: userState?.isActive,
      shouldRunAuthorizeUser,
    }
  );

  if (
    !mfeMode &&
    enableAtkomst &&
    (isFixaBasicPermissionsError || isPlatformOwnerError)
  ) {
    reportMessageToSentry(
      "AuthorizedUserRoute: Atkomst permission queries failed",
      {
        pathname,
        isFixaBasicPermissionsError,
        isPlatformOwnerError,
      },
      "error"
    );
    return <Error isAccessIssue={false} />;
  }

  if (
    !mfeMode &&
    enableAtkomst &&
    !hasFixaBasicPermissions &&
    !userState.isError
  ) {
    addSentryBreadcrumb(
      "AuthorizedUserRoute: enableAtkomst without fixa.basic -> Unauthorized",
      "auth-routing",
      { pathname }
    );
    return <UnauthorizedPage />;
  }

  if (!shouldRunAuthorizeUser && isOnAtkomstOnboardingPath) {
    return children;
  }

  if (
    shouldRunAuthorizeUser &&
    !mfeMode &&
    !enableAtkomst &&
    !hasFixaBasicPermissions &&
    userState.isError &&
    userState.errorType === ErrorType.FORBIDDEN
  ) {
    return <Error isAccessIssue />;
  }

  // Redirect to NewUserPage for users that are not fully provisioned yet (Åtkomst markets only).
  // Guarded by hasFixaBasicPermissions (JWT-derived) rather than enableAtkomst (DB-derived flag)
  // so this works for brand-new Atkomst users who don't have a DB entry yet.
  // Allow /newuser and /newuserteamselection to pass through so onboarding flow renders.
  if (
    shouldRunAuthorizeUser &&
    !mfeMode &&
    hasFixaBasicPermissions &&
    userState.errorType === ErrorType.NOTFOUND
  ) {
    addSentryBreadcrumb(
      "AuthorizedUserRoute: fixa.basic + NOTFOUND -> NewUserPage flow",
      "auth-routing",
      { pathname }
    );
    return isOnAtkomstOnboardingPath ? (
      children
    ) : (
      <Navigate to="/newuser" replace />
    );
  }

  // User has no Atkomst permission and either no DB entry (404) or backend rejected
  // provisioning (403). For users without fixa.basic, show Unauthorized so they can
  // request access via My Identity.
  if (
    shouldRunAuthorizeUser &&
    !mfeMode &&
    !hasFixaBasicPermissions &&
    (userState.errorType === ErrorType.NOTFOUND ||
      (userState.isError && userState.errorType === ErrorType.FORBIDDEN))
  ) {
    addSentryBreadcrumb(
      "AuthorizedUserRoute: no fixa.basic + NOTFOUND/FORBIDDEN -> Unauthorized",
      "auth-routing",
      { pathname, userStateErrorType: userState.errorType }
    );
    return <UnauthorizedPage />;
  }

  // PLATFORM_OWNER users are not PAOM store employees so they always get 403 from the
  // provisioning backend. Send them to NewUserPage to self-select a virtual team store.
  if (
    shouldRunAuthorizeUser &&
    !mfeMode &&
    hasFixaBasicPermissions &&
    hasPlatformOwnerAuthorization &&
    userState.isError &&
    userState.errorType === ErrorType.FORBIDDEN
  ) {
    addSentryBreadcrumb(
      "AuthorizedUserRoute: PLATFORM_OWNER + FORBIDDEN -> NewUserPage flow",
      "auth-routing",
      { pathname }
    );
    return isOnAtkomstOnboardingPath ? (
      children
    ) : (
      <Navigate to="/newuser" replace />
    );
  }

  // User has fixa.basic (valid Atkomst token, possibly cached) but backend cannot provision
  // them — no store assignment in PAOM, or they were removed from Atkomst/DB and the token
  // is still within its validity window. The backend has definitively rejected provisioning,
  // so show UnauthorizedPage regardless of the cached token state.
  if (
    shouldRunAuthorizeUser &&
    !mfeMode &&
    hasFixaBasicPermissions &&
    userState.isError &&
    userState.errorType === ErrorType.FORBIDDEN
  ) {
    addSentryBreadcrumb(
      "AuthorizedUserRoute: fixa.basic + FORBIDDEN -> Unauthorized",
      "auth-routing",
      { pathname }
    );
    return <UnauthorizedPage />;
  }

  if (
    shouldRunAuthorizeUser &&
    (userState.isError || userState.errorType === ErrorType.FORBIDDEN)
  ) {
    reportMessageToSentry(
      "AuthorizedUserRoute: unhandled userState error -> Error page",
      { pathname, userStateErrorType: userState.errorType },
      "warning"
    );
    return (
      <Error isAccessIssue={userState.errorType === ErrorType.FORBIDDEN} />
    );
  }

  if (shouldRunAuthorizeUser && !userState?.isActive) {
    reportMessageToSentry(
      "AuthorizedUserRoute: userState inactive with no error -> Error page",
      { pathname },
      "warning"
    );
  }

  return shouldRunAuthorizeUser ? (
    userState?.isActive ? (
      children
    ) : (
      <Error isAccessIssue={false} />
    )
  ) : (
    children
  );
};
